import { z } from 'zod';
import { MemoryKind } from './enums.ts';
import { brandedUuid } from './ids.ts';

/**
 * Memory (gravity) contracts — the typed shapes that flow across the memory
 * seam: what the extractor produces and what the post-assistant-turn extraction
 * job carries. One source of truth so the backend extractor, the `apps/jobs`
 * worker, and whoever enqueues (the chat run path) agree on the wire shape.
 *
 * Recall/write persistence lives behind `MemoryService` in `apps/backend`; the
 * durable rows live in `@4f/db`'s `memories` table scoped to
 * `(account_id, girlfriend_id)`. Embeddings / `pgvector` recall is a LATER
 * migration — these contracts are the recency-ranked seam that ships now.
 *
 * See docs/idea/data-model.md § "Memory (gravity)" + docs/mvp-v1.0.0/phase-4.
 */

/**
 * The hard ceiling on either side of a `memory.extract` payload. Exported (not
 * inlined in the schema) because the PRODUCER has to know it: the job is only
 * validated at the worker, so an over-long turn used to enqueue cleanly and then
 * fail `MemoryExtractJobData.parse` five times — burning every BullMQ attempt and
 * losing the memory in silence, for exactly the long, detail-dense replies most
 * worth remembering. Callers clamp with {@link clampMemoryText} before enqueueing.
 */
export const MEMORY_TEXT_MAX = 20_000;

/**
 * Cut `text` down to {@link MEMORY_TEXT_MAX} characters, keeping the HEAD (an
 * assistant turn opens on what it is about; a truncated tail costs less than a
 * dropped job). A cut that would land inside a surrogate pair steps back one unit
 * so the payload never carries a lone surrogate into JSON. Idempotent, and a
 * no-op for anything already within the cap.
 */
export function clampMemoryText(text: string): string {
  if (text.length <= MEMORY_TEXT_MAX) {
    return text;
  }
  const last = text.charCodeAt(MEMORY_TEXT_MAX - 1);
  const splitsPair = last >= 0xd800 && last <= 0xdbff;
  return text.slice(0, splitsPair ? MEMORY_TEXT_MAX - 1 : MEMORY_TEXT_MAX);
}

/**
 * One durable memory the extractor pulls from an assistant turn — a single fact,
 * preference, or rolling summary as a short natural-language snippet. `text` is
 * length-bounded so a runaway model can't write an essay into one row; it is
 * stored as the `memories.content` jsonb (`{ text }`).
 */
export const ExtractedMemory = z.object({
  kind: MemoryKind,
  text: z.string().trim().min(1).max(500),
});
export type ExtractedMemory = z.infer<typeof ExtractedMemory>;

/**
 * The BullMQ payload for `memory.extract` — enqueued after an assistant turn is
 * persisted. It carries the scope (`accountId`/`girlfriendId`), the source
 * message id (`memories.source_msg_id` + the idempotency key), and the turn text
 * the extractor reads, so the worker is self-contained and needs no DB read to
 * do its work. Every identifier is validated as a branded UUID v7 at the queue
 * boundary, so a malformed job is rejected before delivery, not after.
 */
export const MemoryExtractJobData = z.object({
  accountId: brandedUuid('AccountId'),
  girlfriendId: brandedUuid('GirlfriendId'),
  conversationId: brandedUuid('ConversationId'),
  /** The assistant message this extraction is derived from (idempotency anchor). */
  sourceMessageId: brandedUuid('MessageId'),
  /** The user turn that prompted the reply — context for extraction. */
  userText: z.string().max(MEMORY_TEXT_MAX),
  /** The assistant reply the durable facts are pulled from. */
  assistantText: z.string().min(1).max(MEMORY_TEXT_MAX),
});
export type MemoryExtractJobData = z.infer<typeof MemoryExtractJobData>;
